import React from "react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { RoleType, getRouteWithId } from "../CreateButton/constants";
import { useProjects } from "../../hooks/useBonusQueries";
import { BonusApplication } from "../../services/bonus/types";
import { Timeline, TimelineDetail } from "../Timeline/";
import { RejectionBanner } from "../RejectionBanner";
import { getTimelineEvents, formatStatusLabel, getRejectionInfo } from "../../utils/timeline/timelineHelpers";

interface ApplicationItemProps {
  app: BonusApplication;
  expandedRows: Set<number>;
  toggleRowExpansion: (id: number) => void;
  roleType: RoleType;
  showActions?: boolean;
  isReviewMode?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  onSave?: () => void;
  onSubmit?: () => void;
  onPayrollProcessing?: () => void;
  onPaid?: () => void;
  onCheckboxChange?: (id: number, checked: boolean) => void;
  showCheckbox?: boolean;
  isChecked?: boolean;
  sourcePath?: string;
}

export const ApplicationItem: React.FC<ApplicationItemProps> = ({
  app,
  expandedRows,
  toggleRowExpansion,
  roleType,
  isReviewMode = false,
  onCheckboxChange,
  showCheckbox,
  isChecked = false,
  sourcePath,
}) => {
  const navigate = useNavigate();
  const isExpanded = expandedRows.has(app.bonusApplicationId);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (onCheckboxChange) {
      onCheckboxChange(app.bonusApplicationId, checked);
    }
  };

  const timelineEvents = getTimelineEvents(app.applicationType, app);
  const rejectionInfo = getRejectionInfo(app);
  const isRejected = app.currentStatus === "Rejected";

  const handleItemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const applicationType = app.applicationType.startsWith("CCM3Manager")
      ? "DCCM"
      : app.applicationType.startsWith("CCM")
        ? "CCM"
        : app.applicationType.trim();
    const baseRoute = getRouteWithId(
      app.applicationType.startsWith("CCM3Manager") ? RoleType.CCM3Manager : roleType,
      app.bonusApplicationId,
      applicationType
    );
    const route = isReviewMode ? `${baseRoute}?mode=review` : baseRoute;
    navigate(route, { state: { from: sourcePath } });
  };

  const { data: projectsData } = useProjects();

  return (
    <div className="border-b border-gray-200">
      {/* Rejection Banner - Show at top if rejected */}
      {isRejected && rejectionInfo && (
        <div className="lg:hidden">
          <RejectionBanner
            rejectedBy={rejectionInfo.rejectedBy}
            rejectedOn={rejectionInfo.rejectedOn}
            rejectionReason={rejectionInfo.rejectionReason}
            rejectedAt={rejectionInfo.rejectedAt}
            variant="banner"
          />
        </div>
      )}

      {/* Mobile/Tablet Layout - Vertical Timeline */}
      <div className="lg:hidden">
        <div className="relative py-3 px-2 sm:px-4 hover:bg-gray-50">
          {/* Header Section - Employee Info */}
          <div className="flex items-start gap-2 mb-4">
            {/* Checkbox */}
            {showCheckbox && (
              <div className="flex-shrink-0 pt-1">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 border border-gray-300 rounded-md accent-blue-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                />
              </div>
            )}

            {/* Employee Info */}
            <div className="flex-1 min-w-0" onClick={handleItemClick}>
              <div className="flex items-center gap-2 mb-1">
                <div className={`flex-none rounded-full p-1 ${isRejected ? "text-red-600 bg-red-100" : "text-gray-600 bg-gray-100"
                  }`}>
                  <div className="h-2 w-2 rounded-full bg-current" />
                </div>
                <h2 className="text-sm font-semibold text-gray-900 truncate">
                  {app.employeeName}
                </h2>
              </div>
              <p className={`text-xs font-bold ${isRejected ? "text-red-700" : "text-gray-600"
                }`}>
                {app.year} Q{app.quarter} • {formatStatusLabel(app?.currentStatus)}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {app.applicationType} - ${app?.amount?.toLocaleString() ?? "0"}
              </p>
              {app.location && (
                <p className="text-xs text-gray-500 group-hover:text-blue-500/60 transition-colors duration-200 truncate">
                  {app.location}
                </p>
              )}
              {app.projectNumbers && (
                <p className="text-xs text-gray-500 truncate mt-1">
                  Project: [
                  {app.projectNumbers
                    .split(",")
                    .map((projectNumber) => {
                      const project = projectsData?.find(
                        (p) => p.projectNumber === projectNumber.trim()
                      );
                      return project
                        ? `${project.projectName} - ${project.projectNumber}`
                        : projectNumber.trim();
                    })
                    .join(", ")}
                  ]
                </p>
              )}

              {app.hasOverriddenHierarchy && (
                <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 mt-1 inline-flex items-center gap-1">
                  <span>⚠</span> Override Active
                </p>
              )}
            </div>

            {/* Expand Icon */}
            <div
              className="flex-shrink-0 pt-1 cursor-pointer"
              onClick={() => toggleRowExpansion(app.bonusApplicationId)}
            >
              {isExpanded ? (
                <ExpandLess className="h-5 w-5 text-gray-400" />
              ) : (
                <ExpandMore className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Desktop Layout - Horizontal Timeline */}
      <div className="hidden lg:block">
        {/* Rejection Banner - Desktop */}
        {isRejected && rejectionInfo && (
          <RejectionBanner
            rejectedBy={rejectionInfo.rejectedBy}
            rejectedOn={rejectionInfo.rejectedOn}
            rejectionReason={rejectionInfo.rejectionReason}
            rejectedAt={rejectionInfo.rejectedAt}
            variant="banner"
          />
        )}

        <div className="relative flex items-center space-x-4 py-4 hover:bg-gray-50 cursor-pointer min-h-[100px]">
          {/* Checkbox */}
          {showCheckbox && (
            <div className="pl-4">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
                className="h-5 w-5 border border-gray-300 rounded-md accent-blue-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
              />
            </div>
          )}

          {/* Left side - Employee info */}
          <div className="min-w-0 flex-auto pl-8 flex items-center gap-x-6 group cursor-pointer relative rounded-lg hover:bg-blue-50/30 transition-all duration-200 p-3 hover:shadow-sm hover:-translate-y-[1px]">
            <div className="flex-none w-48">
              <div
                className="flex items-center gap-x-3"
                onClick={handleItemClick}
              >
                <div className={`flex-none rounded-full p-1 transition-all duration-200 transform group-hover:scale-110 ${isRejected
                    ? "text-red-600 bg-red-100 group-hover:bg-red-200/50"
                    : "text-gray-600 bg-gray-100 group-hover:bg-blue-100/50 group-hover:text-blue-500"
                  }`}>
                  <div className="h-2 w-2 rounded-full bg-current" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold leading-6 text-gray-900 group-hover:text-blue-600 transition-colors duration-200 truncate">
                    {app.employeeName}
                  </h2>
                  <p className="text-xs font-bold text-gray-600 group-hover:text-blue-500/60 transition-colors duration-200">
                    {app.year} Q{app.quarter}
                  </p>
                  <p className={`text-xs font-bold transition-colors duration-200 ${isRejected
                      ? "text-red-700"
                      : "text-gray-600 group-hover:text-blue-500/60"
                    }`}>
                    Status: {formatStatusLabel(app?.currentStatus)}
                  </p>
                  <p className="text-xs text-gray-500 group-hover:text-blue-500/60 transition-colors duration-200 truncate">
                    {app.applicationType} - $
                    {app?.amount?.toLocaleString() ?? "0"}
                  </p>
                  {app.location && (
                    <p className="text-xs text-gray-500 truncate">
                      {app.location}
                    </p>
                  )}
                  {app.projectNumbers && (
                    <p className="text-xs text-gray-500 group-hover:text-blue-500/60 transition-colors duration-200 truncate">
                      Project: [
                      {app.projectNumbers
                        .split(",")
                        .map((projectNumber) => {
                          const project = projectsData?.find(
                            (p) => p.projectNumber === projectNumber.trim()
                          );
                          return project
                            ? `${project.projectName} - ${project.projectNumber}`
                            : projectNumber.trim();
                        })
                        .join(", ")}
                      ]
                    </p>
                  )}

                  {app.hasOverriddenHierarchy && (
                    <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 mt-1 inline-flex items-center gap-1">
                      <span>⚠</span> Override Active
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Horizontal Timeline */}
            <div
              className="flex-1 py-2"
              onClick={() => toggleRowExpansion(app.bonusApplicationId)}
            >
              <Timeline
                events={timelineEvents}
                currentStatus={app.currentStatus}
                size="md"
                showLabels={true}
              />
            </div>
          </div>

          <div className="pr-4 cursor-pointer" onClick={() => toggleRowExpansion(app.bonusApplicationId)}>
            {isExpanded ? (
              <ExpandLess className="h-5 w-5 text-gray-400" />
            ) : (
              <ExpandMore className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded timeline details - Same for all screen sizes */}
      {isExpanded && (
        <div className="bg-gray-50 transition-all duration-200 ease-in-out">
          <div className="ml-auto px-4 sm:px-8 py-4 sm:py-6">
            {/* Show rejection details if rejected */}
            {isRejected && rejectionInfo && (
              <div className="mb-6">
                <RejectionBanner
                  rejectedBy={rejectionInfo.rejectedBy}
                  rejectedOn={rejectionInfo.rejectedOn}
                  rejectionReason={rejectionInfo.rejectionReason}
                  rejectedAt={rejectionInfo.rejectedAt}
                  variant="card"
                />
              </div>
            )}

            {/* Desktop: Card view, Mobile: Vertical timeline with all steps */}
            <div className="hidden lg:block">
              <TimelineDetail
                events={timelineEvents}
                currentStatus={app.currentStatus}
                variant="card"
                showAllSteps={true}
              />
            </div>
            <div className="lg:hidden">
              <TimelineDetail
                events={timelineEvents}
                currentStatus={app.currentStatus}
                variant="list"
                showAllSteps={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};